// frontend/src/pages/CreditCards.js

import React, { useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  CreditCard,
  Crown,
  Star,
  ShieldCheck,
  ArrowRight,
  Phone,
  CheckCircle2,
} from "lucide-react";

import SEO from "../components/SEO";

const CreditCards = () => {
  const navigate = useNavigate();

  const waLink = useMemo(() => {
    const text =
      "Hi OJOVEST - I am interested in Elite Credit Cards. Please guide me for eligibility and the best option.";
    return `https://wa.me/918651035362?text=${encodeURIComponent(text)}`;
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* ✅ SEO */}
      <SEO
        title="Elite Credit Cards | OJOVEST"
        description="Explore elite credit cards with premium rewards, lounge access and concierge benefits. OJOVEST helps with eligibility guidance and application support."
        canonicalUrl="https://www.ojovest.com/credit-cards"
        image="/og-image.png"
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-[#354F37]">
            OJOVEST
          </Link>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="hidden sm:inline-flex bg-[#F8F4EF] border border-gray-200 text-[#354F37] px-4 py-2 rounded-lg font-semibold hover:bg-white transition"
            >
              Dashboard
            </button>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#20BA5A] transition"
            >
              <Phone size={18} />
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm text-[#444444]">
            <Crown size={16} className="text-[#354F37]" />
            Premium • Rewards • Concierge
          </div>

          <h1 className="mt-6 text-4xl md:text-6xl font-bold text-[#222222] leading-tight">
            Elite Credit Cards
            <br />
            <span className="text-[#354F37]">For Premium Lifestyle</span>
          </h1>

          <p className="mt-5 text-lg md:text-xl text-[#444444] max-w-3xl mx-auto">
            Get guidance on eligibility and the right card based on your income,
            spends, and profile. Fast, discreet and relationship-led support.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/register")}
              className="bg-[#354F37] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#426045] transition inline-flex items-center justify-center gap-2"
            >
              Check Eligibility <ArrowRight size={18} />
            </button>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-[#354F37] text-[#354F37] px-8 py-4 rounded-lg font-semibold hover:bg-[#F8F4EF] transition inline-flex items-center justify-center gap-2"
            >
              Talk to Advisor <Phone size={18} />
            </a>
          </div>

          <p className="mt-4 text-xs text-[#666]">
            *Approvals are at bank discretion. Terms and eligibility vary by
            issuer.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <Star className="text-[#354F37] mb-4" size={40} />
            <h3 className="text-xl font-bold text-[#222222] mb-2">
              Premium Rewards
            </h3>
            <p className="text-[#444444]">
              Earn accelerated points, travel benefits and exclusive offers.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <ShieldCheck className="text-[#354F37] mb-4" size={40} />
            <h3 className="text-xl font-bold text-[#222222] mb-2">
              Lounge & Concierge
            </h3>
            <p className="text-[#444444]">
              Airport lounge access, concierge services and lifestyle privileges.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm">
            <CreditCard className="text-[#354F37] mb-4" size={40} />
            <h3 className="text-xl font-bold text-[#222222] mb-2">
              Best-fit Recommendation
            </h3>
            <p className="text-[#444444]">
              Guidance to choose the right card based on your profile and needs.
            </p>
          </div>
        </div>
      </section>

      {/* Cards list */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#222222] text-center mb-10">
            Popular Elite Cards (Examples)
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Card 1 */}
            <div className="border border-gray-200 rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-[#222222] flex items-center gap-2">
                <Crown className="text-[#354F37]" size={22} />
                Premium Lifestyle Cards
              </h3>
              <ul className="mt-5 space-y-3 text-[#444444]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  American Express Platinum (eligibility-based)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  HDFC Infinia / Diners Black (profile-based)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Axis premium variants (subject to issuer policies)
                </li>
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/register")}
                  className="w-full bg-[#354F37] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#426045] transition"
                >
                  Apply / Eligibility Check
                </button>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center bg-white border border-[#354F37] text-[#354F37] px-6 py-3 rounded-lg font-semibold hover:bg-[#F8F4EF] transition"
                >
                  WhatsApp Now
                </a>
              </div>
            </div>

            {/* Card 2 */}
            <div className="border border-gray-200 rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-[#222222] flex items-center gap-2">
                <Star className="text-[#354F37]" size={22} />
                Travel & Rewards Focus
              </h3>
              <ul className="mt-5 space-y-3 text-[#444444]">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Travel points, hotel & airline partnerships
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Airport lounge access and priority services
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="text-[#354F37] mt-1" size={18} />
                  Concierge & lifestyle benefits (varies by card)
                </li>
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/register")}
                  className="w-full bg-[#354F37] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#426045] transition"
                >
                  Get Recommendations
                </button>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center bg-white border border-[#354F37] text-[#354F37] px-6 py-3 rounded-lg font-semibold hover:bg-[#F8F4EF] transition"
                >
                  Talk to Advisor
                </a>
              </div>
            </div>
          </div>

          {/* Trust note */}
          <div className="mt-10 bg-[#F8F4EF] border border-gray-200 rounded-xl p-6 text-[#444444]">
            <p className="font-semibold text-[#222222] mb-2">Important Note</p>
            <p className="text-sm leading-relaxed">
              OJOVEST provides guidance and facilitation. Final eligibility,
              approval, fees and benefits are decided by the issuer/bank.
              We never ask for OTP, PIN or card passwords.
            </p>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-14 bg-[#354F37] text-white text-center">
        <h2 className="text-3xl font-bold">Want the best elite card for your profile?</h2>
        <p className="mt-3 text-gray-200">
          Share your income & spending range — we’ll suggest the best option.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate("/register")}
            className="bg-white text-[#354F37] px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            Start Eligibility Check
          </button>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#354F37] transition"
          >
            WhatsApp Now
          </a>
        </div>
      </section>

      <footer className="bg-[#354F37] py-8 text-center text-gray-200">
        <p className="font-semibold text-white">OJOVEST</p>
        <p className="text-sm mt-2">Elite Credit Cards • Premium Benefits</p>
        <p className="text-xs mt-4">© {new Date().getFullYear()} OJOVEST. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default CreditCards;
